import React from "react";
import { BellIcon, TrashIcon } from "@heroicons/react/20/solid";
import LocationCard from "./LocationCard";
import { variantURL, fallbackToOriginal } from "../utils/imageVariant";

export default function ChatMessage({
  chat,
  currentUserID,
  reactionPickerMessageId,
  setReactionPickerMessageId,
  REACTION_EMOJIS,
  handleReaction,
  handleSoftDelete,
  handlePermanentDelete,
  setViewingImage,
}) {
  return (
    <div
      className={`flex ${chat.userID === currentUserID ? "justify-end" : "justify-start"} mb-4`}
    >
      <div className={`flex flex-col ${chat.userID === currentUserID ? "items-end" : "items-start"}`}>
        {/* Reaction / moderation picker */}
        {reactionPickerMessageId === chat.id && (
          <div className="flex gap-1 mb-1 p-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 h-8 items-center">
            {!chat.deleted && REACTION_EMOJIS.map((emoji) => {
              const hasReacted = chat.reactions?.[emoji]?.includes(currentUserID);
              return (
                <button
                  key={emoji}
                  onClick={() => handleReaction(chat.id, emoji)}
                  className={`w-7 h-7 flex items-center justify-center text-base hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors ${hasReacted ? 'bg-primary/10' : ''}`}
                >
                  {emoji}
                </button>
              );
            })}
            {!chat.deleted && <div className="w-px h-5 bg-gray-200 dark:bg-gray-700 mx-0.5" />}
            {!chat.deleted && (
              <button
                onClick={() => handleSoftDelete(chat.id)}
                title="Delete (replace with 'Message removed')"
                className="w-7 h-7 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => handlePermanentDelete(chat.id)}
              title="Permanently delete (cannot be undone)"
              className="px-1.5 h-7 flex items-center gap-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/40 rounded transition-colors"
            >
              <TrashIcon className="w-4 h-4" />
              <span className="text-[10px] font-medium leading-none">forever</span>
            </button>
          </div>
        )}

        <div
          className={`max-w-xs p-2 rounded-lg relative cursor-pointer ${chat.deleted
            ? "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 italic border border-dashed border-gray-300 dark:border-gray-600"
            : chat.userID === currentUserID
              ? "bg-primary text-white"
              : "bg-slate-200 dark:bg-slate-800"
            }`}
          onClick={() => setReactionPickerMessageId(prev => prev === chat.id ? null : chat.id)}
        >
          {chat.deleted ? (
            <>
              <div className="font-bold mb-1 text-gray-600 dark:text-gray-300 not-italic">{chat.name}</div>
              <div>Message removed</div>
            </>
          ) : (
            <>
              {chat.type === "notify" && (
                <div className={`absolute -top-2 ${chat.userID === currentUserID ? "-left-2" : "-right-2"} bg-white dark:bg-gray-800 rounded-full p-1 shadow-sm border border-gray-200 dark:border-gray-700 z-10`}>
                  <BellIcon className="w-3 h-3 text-primary" />
                </div>
              )}
              <div className="font-bold mb-1">{chat.name}</div>
              {chat.attachments?.map((attachment, idx) => {
                if (attachment.type === 'image') {
                  return (
                    <div key={idx} className="my-1">
                      <img
                        src={variantURL(attachment.url, 600)}
                        onError={fallbackToOriginal(attachment.url)}
                        alt="Chat attachment"
                        className="rounded-lg cursor-pointer max-w-[220px] max-h-[300px] object-cover"
                        onClick={(e) => { e.stopPropagation(); setViewingImage(attachment); }}
                      />
                    </div>
                  );
                }
                if (attachment.type === 'location' && attachment.latitude != null && attachment.longitude != null) {
                  return (
                    <div key={idx} className="my-1" onClick={(e) => e.stopPropagation()}>
                      <LocationCard
                        latitude={attachment.latitude}
                        longitude={attachment.longitude}
                        label={attachment.label}
                        senderName={chat.name?.split(' ')[0] || 'Someone'}
                      />
                    </div>
                  );
                }
                return null;
              })}
              {chat.message && <div>{chat.message}</div>}

              {/* Reaction badges */}
              {chat.reactions && Object.keys(chat.reactions).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {Object.entries(chat.reactions)
                    .filter(([, users]) => users && users.length > 0)
                    .map(([emoji, users]) => (
                      <span
                        key={emoji}
                        onClick={(e) => { e.stopPropagation(); handleReaction(chat.id, emoji); }}
                        className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs cursor-pointer border ${
                          users.includes(currentUserID)
                            ? 'bg-primary/20 border-primary/40'
                            : 'bg-white/80 dark:bg-gray-800/80 border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        {emoji}{users.length > 1 && <span className="text-[10px]">{users.length}</span>}
                      </span>
                    ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
